//@flow
/** @jsx jsx */

import { type Node, useEffect, useRef, useState } from 'react';

import Button from '../Button';
import WatchWindowSize from '../WatchWindowSize';
import { Colors } from '@zapier/theme';
import { jsx } from '@emotion/core';

const Styles = {
  root: {
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'flex-end',
  },
  showAll: {
    whiteSpace: 'normal',
  },
  hideAll: ({ numberOfLines }) => ({
    WebkitBoxOrient: 'vertical',
    // emotion defaults this to a pixel value, so enforce it to be a plain number with a string >:[
    WebkitLineClamp: `${numberOfLines}`,
    display: '-webkit-box',
    lineHeight: 'inherit',
    maxHeight: numberOfLines * 25,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  }),
  readMoreButton: {
    color: Colors.heroine,
    whiteSpace: 'nowrap',
    paddingLeft: '5px',
  },
};

type TruncateWithWindowInfoProps = {
  /** The number of lines that should show before the read more button */
  numberOfLines: number,
  /** The text to render*/
  children: Node,
  /** Inherited prop from WatchWindowSize */
  windowWidth: number,
};

const TruncateWithWindowInfo = ({
  numberOfLines,
  windowWidth,
  children,
}: TruncateWithWindowInfoProps) => {
  const [shouldShowAll, toggleShowAll] = useState(false);
  const [textScrollHeight, updateScrollHeight] = useState(0);
  const [textOffsetHeight, updateOffsetHeight] = useState(0);

  const textRef = useRef(null);

  // the first time the text component mounts, establish the
  // existing offsets.
  // The code below will decide whether or no to show the read more button
  // based on the existing offset to scroll height ratio.
  useEffect(() => {
    const textSpan = textRef.current;
    if (textSpan) {
      updateScrollHeight(textSpan.scrollHeight);
      updateOffsetHeight(textSpan.offsetHeight);
    }
  }, [windowWidth]);

  // Whenever the user clicks 'read more', update to show all the text.
  useEffect(() => {
    const textSpan = textRef.current;
    if (textSpan && shouldShowAll) {
      updateScrollHeight(textSpan.scrollHeight);
      updateOffsetHeight(textSpan.offsetHeight);
    }
  }, [shouldShowAll]);

  const buttonText = shouldShowAll ? 'Hide' : 'Read more';
  const textCss = shouldShowAll
    ? Styles.showAll
    : Styles.hideAll({ numberOfLines });

  const isOverflowingHeight = textScrollHeight > textOffsetHeight;
  const isShowingAllText = textScrollHeight === textOffsetHeight;
  const shouldShowReadMore =
    !shouldShowAll && isOverflowingHeight && !isShowingAllText;

  return (
    <span css={Styles.root}>
      <span css={textCss} ref={textRef}>
        {children}
      </span>
      {shouldShowReadMore ? (
        <span css={Styles.readMoreButton}>
          <Button
            backgroundType="clear"
            color="primary"
            onClick={() => toggleShowAll(!shouldShowAll)}
            size="small"
          >
            {buttonText}
          </Button>
        </span>
      ) : null}
    </span>
  );
};

type Props = {
  /** The number of lines that should show before the read more button */
  numberOfLines: number,
  /** The text to render*/
  children: Node,
};

const TruncateWithReadMore = (props: Props) => (
  <WatchWindowSize>
    {({ windowWidth }) => (
      <TruncateWithWindowInfo {...props} windowWidth={windowWidth} />
    )}
  </WatchWindowSize>
);

TruncateWithReadMore.defaultProps = {
  numberOfLines: 4,
};

export default TruncateWithReadMore;
